import format from 'date-fns/format'
import { TFunction } from 'i18next'

import { status } from 'config/contest'

import { Contest } from 'types/contest/Contest'
import { ContestAssociation } from 'types/contestAssociation/ContestAssociation'
import { ContestFile } from 'types/contestFile/ContestFile'
import { ContestMessage } from 'types/contestMessage/ContestMessage'
import { ContestSide } from 'types/enums/ContestSide'
import { ContestUpdateAction } from 'types/enums/ContestUpdateAction'

import {
  formatDateBR,
  formatDateTimeBR,
  getTextFromHTMLString
} from 'utils/formatUtils'
import { downloadPDF } from 'utils/pdfUtils'

type ContestData = {
  userSide: ContestSide
  contest: Contest
  messages: ContestMessage[]
  files: ContestFile[]
  associations: ContestAssociation[]
}

export function printContest(data: ContestData, translate: TFunction): void {
  function header() {
    const { id } = data.contest

    return {
      alignment: 'center',
      marginBottom: 16,
      columns: [
        {
          image: 'logo',
          fit: [120, 100]
        },
        {
          width: '50%',
          stack: [{ text: `Disputa ${id}`, style: 'title' }]
        },
        {}
      ]
    }
  }

  function contestHeading() {
    const {
      id,
      requester,
      defendant,
      category,
      currentStep,
      createdAt
    } = data.contest

    const createData = [...data.contest.data].sort((a, b) => a.id - b.id)[0]

    return [
      {
        alignment: 'left',
        stack: [
          {
            marginBottom: 4,
            columns: [
              {
                text: [
                  { text: 'Nº da disputa: ', style: 'textBold' },
                  { text: id.toString(), style: 'text' }
                ]
              },
              {
                text: [
                  { text: 'Situação: ', style: 'textBold' },
                  {
                    text: translate(
                      `pages:contestDetails.heading.status.${status[currentStep]}`
                    ),
                    style: 'text'
                  }
                ]
              }
            ]
          },
          {
            marginBottom: 4,
            columns: [
              {
                text: [
                  { text: 'Aberta por: ', style: 'textBold' },
                  {
                    text: `${createData.user.name} ${createData.user.lastname}`,
                    style: 'text'
                  }
                ]
              },
              {
                text: [
                  { text: 'Data de abertura: ', style: 'textBold' },
                  { text: formatDateTimeBR(createdAt), style: 'text' }
                ]
              }
            ]
          },
          {
            marginBottom: 4,
            columns: [
              {
                text: [
                  { text: 'Requerente: ', style: 'textBold' },
                  { text: requester, style: 'text' }
                ]
              },
              {
                text: [
                  { text: 'Requerida: ', style: 'textBold' },
                  { text: defendant, style: 'text' }
                ]
              }
            ]
          },
          {
            columns: [
              {
                text: [
                  { text: 'Categoria: ', style: 'textBold' },
                  { text: category, style: 'text' }
                ]
              }
            ]
          }
        ]
      }
    ]
  }

  function contestData() {
    const { contest, userSide } = data

    const contestUpdates = [...data.contest.data].sort((a, b) => {
      if (a.createdAt > b.createdAt) {
        return 1
      }

      if (a.createdAt < b.createdAt) {
        return -1
      }

      return 0
    })

    return [
      [{ text: 'Acompanhamento', style: 'subtitle', fillColor: '#CCCCCC' }],
      [
        {
          alignment: 'left',
          ol: contestUpdates.map((item, index) => {
            const { text, files, user, createdAt } = item

            let action = ''
            let side = ''
            if (item.action && item.side) {
              action = item.action
              side = item.side
            } else if (text === 'Solved with signatures') {
              action = 'AGREEMENT_SIGNED'
              side = ContestSide.REQUESTER
            } else if (text === 'Boleto processado') {
              action = 'PAYMENT_BOLETO_CONFIRMED'
              side = ContestSide.REQUESTER
            } else if (text === 'Boleto do reenvio processado') {
              action = 'PAY_RESEND'
              side = ContestSide.DEFENDANT
            } else if (text === 'Signed Document') {
              action = 'SIGNED_DOCUMENT'
              side = ''
            } else {
              action = 'CREATED'
              side = ContestSide.REQUESTER
            }

            const title = `pages:contestDetails.tabs.timeline.itemHeading.${userSide}.${action}${
              side ? '.' + side : ''
            }`

            const actionsWithContent = [
              'CREATED',
              ContestUpdateAction.REPLY,
              ContestUpdateAction.REPLICATION,
              ContestUpdateAction.REJOINDER
            ]

            const hiddenActions = ['AGREEMENT_SIGNED']

            return !hiddenActions.includes(action)
              ? {
                  marginTop: index > 0 ? 8 : 0,
                  stack: [
                    {
                      alignment: 'left',
                      stack: [
                        {
                          style: 'subtitle',
                          text: getTextFromHTMLString(
                            translate(title, {
                              contestId: contest.id,
                              requester: contest.requester,
                              defendant: contest.defendant
                            })
                          )
                        },
                        {
                          style: 'textSmall',
                          text: user.name ? `${user.name} ${user.lastname}` : ''
                        },
                        {
                          style: 'textSmall',
                          text: formatDateTimeBR(createdAt)
                        }
                      ]
                    },
                    actionsWithContent.includes(action) && !!text
                      ? {
                          alignment: 'left',
                          marginTop: 2,
                          stack: [
                            { style: 'text', text: getTextFromHTMLString(text) }
                          ]
                        }
                      : null,
                    files.length
                      ? {
                          alignment: 'left',
                          marginTop: 2,
                          style: 'textSmall',
                          ul: files.map((file) => file.filename)
                        }
                      : null
                  ]
                }
              : null
          })
        }
      ]
      // [
      //   [
      //     {
      //       type: 'none',
      //       ul: contestUpdates.map((item) => ({
      //         alignment: 'left',
      //         style: 'text',
      //         marginBottom: 8,
      //         stack: [
      //           {
      //             columns: [
      //               {
      //                 text: `[${formatDateTimeBR(item.createdAt)}]`
      //               },
      //               {
      //                 text: item.user.name
      //                   ? `${item.user.name} ${item.user.lastname}`
      //                   : ''
      //               },
      //               {
      //                 text: item.side
      //               }
      //             ]
      //           },
      //           {
      //             text: item.text
      //               ? new DOMParser().parseFromString(item.text, 'text/html')
      //                   .documentElement.innerText
      //               : ''
      //           }
      //         ]
      //       }))
      //     }
      //   ]
      // ]
    ]
  }

  function contestFiles() {
    const { files } = data

    return [
      [{ text: 'Outros anexos', style: 'subtitle', fillColor: '#CCCCCC' }],
      [
        {
          alignment: 'left',
          style: 'textBold',
          columns: [
            { width: '*', text: 'Arquivo' },
            { width: '25%', text: 'Enviado por' },
            { width: '15%', text: 'Data do Envio' }
          ]
        }
      ],
      [
        [
          files.map((file) => ({
            alignment: 'left',
            style: 'text',
            columns: [
              { width: '*', text: file.filename },
              {
                width: '25%',
                text: `${file.addedBy.name} ${file.addedBy.lastname}`
              },
              { width: '15%', text: formatDateBR(file.createdAt) }
            ]
          }))
        ]
      ]
    ]
  }

  function contestMembers() {
    const { members } = data.contest

    return [
      [{ text: 'Equipe de atuação', style: 'subtitle', fillColor: '#CCCCCC' }],
      [
        {
          alignment: 'left',
          style: 'textBold',
          columns: [
            { width: '*', text: 'Nome' },
            { width: '25%', text: 'Atribuição' },
            { width: '15%', text: 'Data de Cadastro' }
          ]
        }
      ],
      [
        members.map((member) => ({
          alignment: 'left',
          style: 'text',
          columns: [
            {
              width: '*',
              text: `${member.user.name} ${member.user.lastname}`
            },
            {
              width: '25%',
              text: translate(
                `common:contestMemberPermissions.${member.permission}`
              )
            },
            {
              width: '15%',
              text: formatDateBR(member.createdAt)
            }
          ]
        }))
      ]
    ]
  }

  function contestMessages() {
    const messages = data.messages

    return [
      [{ text: 'Mensagens', style: 'subtitle', fillColor: '#CCCCCC' }],
      [
        messages.map((message, index) => ({
          alignment: 'left',
          marginTop: index > 0 ? 8 : 0,
          stack: [
            {
              style: 'textBold',
              columnGap: 4,
              columns: [
                {
                  width: 'auto',
                  text: message.user
                },
                {
                  width: 'auto',
                  text: '-'
                },
                {
                  width: 'auto',
                  text: message.institution
                },
                {
                  width: 'auto',
                  text: '-'
                },
                {
                  width: 'auto',
                  text: translate(`common:contestSide.${message.side}`)
                }
              ]
            },
            {
              style: 'textSmall',
              columns: [
                {
                  width: '*',
                  text: formatDateTimeBR(message.createdAt)
                }
              ]
            },
            {
              style: 'text',
              marginTop: 2,
              columns: [
                {
                  width: '*',
                  text: message.text
                }
              ]
            }
          ]
        }))
      ]
    ]
  }

  function contestAssociations() {
    const { associations } = data

    return [
      [
        { text: 'Disputas vinculadas', style: 'subtitle', fillColor: '#CCCCCC' }
      ],
      [
        {
          alignment: 'left',
          style: 'textBold',
          columns: [
            { width: '10%', text: '#' },
            { width: '*', text: 'Requerente' },
            { width: '*', text: 'Requerida' }
          ]
        }
      ],
      [
        associations.map((item) => ({
          alignment: 'left',
          style: 'text',
          columns: [
            {
              width: '10%',
              text: item.contest
            },
            {
              width: '*',
              text: item.requester
            },
            {
              width: '*',
              text: item.defendant
            }
          ]
        }))
      ]
    ]
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function build(): any {
    return {
      info: {
        title: `Disputa ${data.contest.id}`
      },
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [20, 30, 20, 30],
      content: [
        {
          stack: [
            header(),
            {
              alignment: 'center',
              layout: 'noBorders',
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      table: {
                        widths: ['*'],
                        body: [
                          contestHeading(),
                          ...contestData(),
                          ...contestFiles(),
                          ...contestMembers(),
                          ...contestMessages(),
                          ...contestAssociations()
                        ]
                      }
                    }
                  ]
                ]
              }
            }
          ]
        }
      ],
      footer: function (currentPage: number, pageCount: number) {
        return [
          {
            alignment: 'center',
            style: 'textBoldSmall',
            margin: [20, 8, 20, 8],
            widths: ['*', '*', '*'],
            columns: [
              {
                alignment: 'left',
                text: format(new Date(), 'dd/MM/yyyy HH:mm:ss')
              },
              {
                alignment: 'center',
                decoration: 'underline',
                text: 'https://resolucao.openbankingbrasil.org.br',
                link: 'https://resolucao.openbankingbrasil.org.br'
              },
              {
                alignment: 'right',
                text: `${currentPage} de ${pageCount}`
              }
            ]
          }
        ]
      },
      styles: {
        title: {
          fontSize: 14,
          bold: true,
          marginBottom: 2
        },
        subtitle: {
          fontSize: 11,
          bold: true
        },
        text: {
          fontSize: 10
        },
        textBold: {
          fontSize: 10,
          bold: true
        },
        textSmall: {
          fontSize: 8
        },
        textBoldSmall: {
          fontSize: 8,
          bold: true
        }
      },
      images: {
        logo:
          'data:image/png;base64,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'
      }
    }
  }

  downloadPDF(`Disputa - ${data.contest.id}`, build())
}
